import {
  FORM_VALIDATION_CONDITIONS,
  FORM_VALIDATION_DATA_KEYS,
  FORM_VALIDATION_MODE
} from '@utils/constants/form'
import { isArray, isObject } from '@utils/types'
import i18n from 'i18next'

/**
 * Compare values.
 *
 * @param {array} requirementArray
 * @param {*} requirementValue
 * @param {object} data
 * @returns {boolean}
 */
const compareValues = (requirementArray, requirementValue, data) =>
  requirementArray.some(requirement => {
    if (isArray(requirementValue)) {
      return requirementValue.includes(data[requirement])
    } else if (requirementValue !== undefined) {
      const dataValue = data[requirement]
      // dataValue can be an array (e.g. MultiSelect) -> match if it contains the required value
      return isArray(dataValue)
        ? dataValue.includes(requirementValue)
        : dataValue === requirementValue
    } else {
      return data[requirement] !== undefined
    }
  })

/**
 * Check if the data meets the requirements.
 *
 * @param {Array<String>} requirementArray
 * @param {Array<String> | String} requirementValue
 * @param {Object} data
 * @returns {boolean}
 */
const hasMeetRequirements = (requirementArray, requirementValue, data) => {
  return isObject(requirementValue)
    ? requirementArray.every(requirement =>
        compareValues([requirement], requirementValue[requirement], data)
      )
    : compareValues(requirementArray, requirementValue, data)
}

/**
 * Get the fields that need confirmation based on the validation data.
 *
 * @param {Object} validationData Rules to validate
 * @param {Object} object Original data
 * @param {Object} data Data modified by user
 * @param {String} action edit or delete
 * @returns {Array<String>}
 */
export function getFieldsThatNeedConfirmation(
  validationData,
  action = FORM_VALIDATION_MODE.EDIT,
  object = {},
  data = {}
) {
  /*
   * validationData have the following structure:
   * {
   *  "on_field_change": {
   *     FIELD_NAME: {
   *      "type": "WARNING",
   *      "message": "WARNING_EDIT_POINT_DISABLE_STORAGE",
   *      "condition": { // can be null, null means any change
   *          "operator": "EQUAL",
   *          "value": true
   *      }
   *     }
   *  },
   *  "on_delete": { // same structure as "on_field_change"
   *   ...
   *  }
   * }
   */

  const fieldToCheck =
    action === FORM_VALIDATION_MODE.EDIT
      ? FORM_VALIDATION_DATA_KEYS.ON_FIELD_CHANGE
      : FORM_VALIDATION_DATA_KEYS.ON_DELETE

  if (validationData && validationData[fieldToCheck]) {
    if (action === FORM_VALIDATION_MODE.DELETE) return Object.keys(validationData[fieldToCheck])

    // check if the "on_field_change" or "on_delete" attributes
    // have a field that need confirmation from user
    return Object.keys(validationData[fieldToCheck]).filter(key => {
      // if the key contains a ., it means it's a nested field
      const keys = key.split('.')
      let originalData, dataValue
      if (keys.length > 1) {
        // nested field, we need to get the value from the object
        originalData = keys.reduce((acc, curr) => acc?.[curr], object)
        dataValue = keys.reduce((acc, curr) => acc?.[curr], data)
      } else {
        // simple field
        originalData = object[key]
        dataValue = data[key]
      }

      if (!validationData.on_field_change[key]?.condition) {
        return originalData !== dataValue
      }

      switch (validationData.on_field_change[key].condition?.operator) {
        case FORM_VALIDATION_CONDITIONS.EQUAL:
          return dataValue === validationData.on_field_change[key].condition?.value
          break
        case FORM_VALIDATION_CONDITIONS.INFERIOR:
          return dataValue < validationData.on_field_change[key].condition?.value
          break
        case FORM_VALIDATION_CONDITIONS.SUPERIOR:
          return dataValue > validationData.on_field_change[key].condition?.value
          break
        case FORM_VALIDATION_CONDITIONS.INFERIOR_OR_EQUAL:
          return dataValue <= validationData.on_field_change[key].condition?.value
          break
        case FORM_VALIDATION_CONDITIONS.SUPERIOR_OR_EQUAL:
          return dataValue >= validationData.on_field_change[key].condition?.value
          break
        default:
          return originalData !== dataValue
      }
    })
  }
  return []
}

/**
 * Check if model entry meet dependencies.
 *
 * @param {Object} model
 * @param {Object} data
 * @returns {boolean}
 */
export function hasMeetDependencies(model, data) {
  const { dependsOn, dependsOnValue } = model
  const dependsOnArray = Array.isArray(dependsOn) ? dependsOn : [dependsOn]

  return hasMeetRequirements(dependsOnArray, dependsOnValue, data)
}

/**
 * Check if model entry meet exclusions.
 *
 * @param {Object} model
 * @param {Object} data
 * @returns {boolean}
 */
export function hasMeetExclusions(model, data) {
  const { excludeOn, excludeOnValue } = model
  const excludeOnArray = Array.isArray(excludeOn) ? excludeOn : [excludeOn]

  return !hasMeetRequirements(excludeOnArray, excludeOnValue, data)
}

/**
 * Build the translated messages for a model's extra validation data (the
 * `on_field_change` / `on_delete` rules consumed by `getFieldsThatNeedConfirmation`).
 * For each rule with a `message`, resolves a translation either from the
 * provided `translations` map or by looking up `common.<message, lowercased>`.
 *
 * @param {Object} extraFormData - Model's extra validation data (`on_field_change`/`on_delete` rules).
 * @param {Object} [translations] - Pre-resolved translations keyed by message code, takes precedence over the default lookup.
 * @returns {Object} The original `extraFormData` spread with an added `translations` map keyed by message code.
 */
export function getExtraValidationDataWithTranslations(extraFormData, translations = {}) {
  const modelTranslations = {} // point specific translations

  const attributesToCheck = Object.values(FORM_VALIDATION_DATA_KEYS)

  attributesToCheck.forEach(attr => {
    if (!extraFormData || !extraFormData[attr]) return
    Object.keys(extraFormData[attr]).forEach(key => {
      const message = extraFormData[attr][key]?.message ?? null
      if (!message) return
      modelTranslations[message] =
        translations[message] ?? i18n.t(`common.${message.toLowerCase()}`)
    })
  })

  return {
    ...extraFormData, // include the original extra form data
    translations: {
      // specific translations for model
      ...modelTranslations
    }
  }
}
